import type { TranslationKey } from '../i18n/en'

// Pre-validation #58: activity events (Home → "Actividad reciente") are
// persisted with English `title`/`detail` strings at write time
// (entityOutbox.ts, productionUnitTable.ts, waterTable.ts, taskApi.ts,
// lotApi.ts). Rewriting stored history isn't worth it, so the known shapes
// are translated at display time. Anything unrecognised — e.g. a task
// title copied into `detail` — is shown unchanged.

type Translate = (key: TranslationKey, params?: Record<string, string | number>) => string

const FIXED_TEXT_KEYS: Record<string, TranslationKey> = {
  'Water logs updated': 'activity.text.waterLogsUpdated',
  'Water log deleted': 'activity.text.waterLogDeleted',
  'Production record updated': 'activity.text.productionRecordUpdated',
  'Production records updated': 'activity.text.productionRecordsUpdated',
  'Production record deleted': 'activity.text.productionRecordDeleted',
  'Production table migrated': 'activity.text.productionTableMigrated',
  'Task created': 'activity.text.taskCreated',
  'Task deleted': 'activity.text.taskDeleted',
  'Sync queued': 'activity.text.syncQueued'
}

// One phrase per entity + action so the translation can agree in gender
// ("Lote guardado" / "Instalación guardada").
const ENTITY_SLUGS: Record<string, string> = {
  Facility: 'facility',
  'Production unit': 'productionUnit',
  Task: 'task',
  'Mortality matrix': 'mortalityMatrix',
  'SFR matrix': 'sfrMatrix',
  Lot: 'lot'
}

const ENTITY_ACTION_PATTERN = /^(Facility|Production unit|Task|Mortality matrix|SFR matrix|Lot) (saved|updated|deleted)(?:: (.*))?$/
const QUEUED_ROWS_PATTERN = /^Queued (\d+) rows? for sync$/
const MIGRATED_ROWS_PATTERN = /^Migrated (\d+) rows? from legacy storage into offline DB$/

export const translateActivityText = (text: string | undefined | null, t: Translate): string => {
  if (!text) return ''

  const fixedKey = FIXED_TEXT_KEYS[text]
  if (fixedKey) return t(fixedKey)

  const entityAction = ENTITY_ACTION_PATTERN.exec(text)
  if (entityAction) {
    const [, entity, action, name] = entityAction
    const base = t(`activity.${ENTITY_SLUGS[entity]}.${action}` as TranslationKey)
    return name ? `${base}: ${name}` : base
  }

  const queued = QUEUED_ROWS_PATTERN.exec(text)
  if (queued) return t('activity.text.queuedRows', { count: queued[1] })

  const migrated = MIGRATED_ROWS_PATTERN.exec(text)
  if (migrated) return t('activity.text.migratedRows', { count: migrated[1] })

  return text
}
